/**
 * Protected Route component with role-based access control
 */

import React from "react";
import { Navigate } from "react-router-dom";
import { useAuthStore } from "../stores/authStore";
import { useRBAC } from "../hooks/useRBAC";
import { type Module } from "../utils/rolePermissions";

interface ProtectedRouteProps {
  children: React.ReactNode;
  module?: Module;
  requiredAction?: "canRead" | "canCreate" | "canUpdate" | "canDelete";
  /** If set, user role must be one of these (case-insensitive). */
  requireRoles?: string[];
  fallbackPath?: string;
}

const normalizeRoleKey = (role?: string | null) => {
  const r = String(role || "").toUpperCase();
  if (r === "SUPERADMIN") return "SUPER_ADMIN";
  return r;
};

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  module,
  requiredAction = "canRead",
  requireRoles,
  fallbackPath = "/dashboard",
}) => {
  const { isAuthenticated, user } = useAuthStore();
  const { checkPermission } = useRBAC();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requireRoles?.length) {
    const role = normalizeRoleKey(user?.role);
    const allowed = new Set(requireRoles.map((r) => normalizeRoleKey(r)));
    if (!allowed.has(role)) {
      return <Navigate to={fallbackPath} replace />;
    }
  }

  if (module) {
    const hasPermission = checkPermission(module, requiredAction);
    if (!hasPermission) {
      return <Navigate to={fallbackPath} replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
